import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import OpenPlayToolsLayout from '../../components/openplay/OpenPlayToolsLayout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { Checkbox } from '../../components/ui/Checkbox';
import { supabase } from '../../lib/supabaseClient';
import BracketResultRenderer from '../../components/bracket/BracketResultRenderer';
import StepIndicator from '../session-creation-wizard/components/StepIndicator';

const defaultGameMinutesBySport = {
  pickleball: 15,
};

const modeOptions = [
  { value: 'fixed_team', label: '팀 고정 모드' },
  { value: 'rotation', label: '로테이션 모드' }
];

const fixedTeamMatchTypeOptions = [
  { value: 'queue', label: '순차 매칭(빠름)' },
  { value: 'round_robin', label: '라운드로빈(풀리그)' }
];

const oddHandlingOptions = [
  { value: 'balanced', label: '쉬는 사람 균형(권장)' },
  { value: 'random', label: '랜덤' }
];

const formatOptions = [
  { value: 'round_robin', label: '리그전(라운드 로빈)' },
  { value: 'single_elimination', label: '토너먼트(단판 토너먼트)' },
  { value: 'swiss', label: '스위스' }
];

const layoutOptions = [
  { value: 'rounds_table', label: '라운드별 테이블' },
  { value: 'schedule', label: '시간표(코트/시간)' },
  { value: 'tree', label: '트리형(토너먼트)' }
];

const normalizeKey = (a, b) => {
  if (!a || !b) return '';
  return [a, b].sort().join('::');
};

const shuffle = (arr) => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const encodeBase64Url = (obj) => {
  try {
    const json = JSON.stringify(obj);
    const utf8 = encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16)));
    const base64 = btoa(utf8);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  } catch (e) {
    return '';
  }
};

const createNewBracket = () => {
  return {
    name: '새 대진표',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    config: {
      sport: 'pickleball',
      overrideGameMinutesEnabled: false,
      overrideGameMinutes: '',
      totalMinutes: '120',
      courtCount: '2',
      oddHandling: 'balanced',
      mode: 'fixed_team',
      fixedTeamMatchType: 'queue'
    },
    selectedMemberIds: [],
    manualPlayers: [],
    result: null
  };
};

const BracketBuilder = () => {
  const navigate = useNavigate();
  const { bracketId } = useParams();
  const effectiveBracketId = bracketId || 'new';
  const isNewDraft = effectiveBracketId === 'new';
  const [members, setMembers] = useState([]);
  const [brackets, setBrackets] = useState([]);
  const [activeBracketId, setActiveBracketId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [bracketNameDraft, setBracketNameDraft] = useState('');
  const [aiPromptDraft, setAiPromptDraft] = useState('');
  const [isMemberPickerOpen, setIsMemberPickerOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [isSessionPickerOpen, setIsSessionPickerOpen] = useState(false);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isManualInputOpen, setIsManualInputOpen] = useState(false);
  const hydratedRef = useRef(false);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    if (isMemberPickerOpen || isSessionPickerOpen || isManualInputOpen) {
      document.body.style.overflow = 'hidden';
      if (isMemberPickerOpen) setMemberSearch('');
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMemberPickerOpen, isSessionPickerOpen, isManualInputOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape') {
        if (isMemberPickerOpen) setIsMemberPickerOpen(false);
        if (isSessionPickerOpen) setIsSessionPickerOpen(false);
        if (isManualInputOpen) setIsManualInputOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMemberPickerOpen, isSessionPickerOpen, isManualInputOpen]);

  const openSessionPicker = async () => {
    setIsSessionPickerOpen(true);
    setIsLoadingSessions(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('session_invites')
        .select('id, title, venue, date, start_time, participants')
        .gte('date', today)
        .order('date', { ascending: true });

      if (error) {
        console.error('Failed to fetch sessions:', error);
        setUpcomingSessions([]);
      } else {
        setUpcomingSessions(data || []);
      }
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const importSessionParticipants = (session) => {
    const participants = Array.isArray(session?.participants) ? session.participants : [];
    
    const newManualPlayers = participants.map(p => ({
      id: `session_${session?.id}_${p?.phone || Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name: p?.name || '',
      phone: p?.phone || ''
    })).filter(p => p.name);

    const existingIds = new Set(manualPlayers.map(p => `${p.name}_${p.phone}`));
    const toAdd = newManualPlayers.filter(p => !existingIds.has(`${p.name}_${p.phone}`));

    if (toAdd.length > 0) {
      updateActiveBracket({ manualPlayers: [...manualPlayers, ...toAdd] });
    }
    setIsSessionPickerOpen(false);
  };

  const [currentStep, setCurrentStep] = useState(0);
  const steps = useMemo(() => {
    return [
      { id: 'players', title: '참가자', icon: 'Users' },
      { id: 'settings', title: '설정', icon: 'Settings' },
      { id: 'generate', title: '생성/미리보기', icon: 'Swords' },
      { id: 'complete', title: '완료', icon: 'CheckCircle' }
    ];
  }, []);

  const activeBracket = useMemo(() => {
    if (!activeBracketId) return null;
    return brackets.find(b => b?.id === activeBracketId) || null;
  }, [brackets, activeBracketId]);

  const selectedMemberIds = activeBracket?.selectedMemberIds ?? [];
  const manualPlayers = activeBracket?.manualPlayers ?? [];
  const sport = 'pickleball';
  const overrideGameMinutesEnabled = activeBracket?.config?.overrideGameMinutesEnabled ?? false;
  const overrideGameMinutes = activeBracket?.config?.overrideGameMinutes ?? '';
  const totalMinutes = activeBracket?.config?.totalMinutes ?? '120';
  const courtCount = activeBracket?.config?.courtCount ?? '2';
  const oddHandling = activeBracket?.config?.oddHandling ?? 'balanced';
  const mode = activeBracket?.config?.mode ?? 'fixed_team';
  const fixedTeamMatchType = activeBracket?.config?.fixedTeamMatchType ?? 'queue';
  const format = activeBracket?.config?.format ?? 'round_robin';
  const displayLayout = activeBracket?.config?.displayLayout ?? 'rounds_table';
  const startTime = activeBracket?.config?.startTime ?? '';
  const result = activeBracket?.result ?? null;

  useEffect(() => {
    const loadMembersAndBracket = async () => {
      setIsLoading(true);
      setLoadError('');
      hydratedRef.current = false;
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;
        if (!userId) {
          setMembers([]);
          setBrackets([]);
          setActiveBracketId(null);
          setBracketNameDraft('');
          setLoadError('로그인이 필요합니다.');
          return;
        }

        const { data: memberRows, error: memberError } = await supabase
          .from('members')
          .select('*')
          .order('registered_at', { ascending: false });

        if (memberError) {
          setMembers([]);
        } else {
          const mappedMembers = (memberRows || []).map((m) => ({
            id: m?.id,
            name: m?.name,
            phone: m?.phone
          }));
          setMembers(mappedMembers);
        }

        if (effectiveBracketId === 'new') {
          const fresh = createNewBracket();
          const draft = {
            id: 'draft',
            name: fresh.name,
            createdAt: fresh.createdAt,
            updatedAt: fresh.updatedAt,
            config: fresh.config,
            selectedMemberIds: fresh.selectedMemberIds,
            manualPlayers: fresh.manualPlayers,
            result: fresh.result
          };
          setBrackets([draft]);
          setActiveBracketId('draft');
          setBracketNameDraft(draft.name || '');
          setCurrentStep(0);
          hydratedRef.current = true;
          return;
        }

        if (effectiveBracketId) {
          const { data: row, error: bracketError } = await supabase
            .from('brackets')
            .select('*')
            .eq('id', effectiveBracketId)
            .single();

          if (bracketError) {
            setLoadError(bracketError?.message || '대진표를 불러오지 못했습니다.');
            setBrackets([]);
            setActiveBracketId(null);
            setBracketNameDraft('');
            return;
          }

          const rowParticipants = row?.participants || {};
          const loaded = {
            id: row?.id,
            name: row?.name,
            createdAt: row?.created_at,
            updatedAt: row?.updated_at,
            config: row?.config || {},
            selectedMemberIds: rowParticipants?.selectedMemberIds || [],
            manualPlayers: rowParticipants?.manualPlayers || [],
            result: row?.result || null
          };

          setBrackets([loaded]);
          setActiveBracketId(loaded.id);
          setBracketNameDraft(loaded.name || '');
          setCurrentStep(0);
          hydratedRef.current = true;
          return;
        }

        setLoadError('잘못된 접근입니다.');
        setBrackets([]);
        setActiveBracketId(null);
        setBracketNameDraft('');
      } finally {
        setIsLoading(false);
      }
    };

    loadMembersAndBracket();
  }, [effectiveBracketId, navigate]);

  useEffect(() => {
    if (!activeBracketId) return;
    const b = brackets?.find(x => x?.id === activeBracketId);
    setBracketNameDraft(b?.name || '');
    setAiPromptDraft(b?.config?.aiPrompt || '');
  }, [activeBracketId, brackets]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    if (!activeBracketId) return;
    const b = brackets?.find(x => x?.id === activeBracketId);
    if (!b) return;
    if (!b?.id || b?.id === 'draft') return;

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(async () => {
      try {
        const payload = {
          name: b?.name || '대진표',
          sport: 'pickleball',
          config: b?.config || {},
          participants: {
            selectedMemberIds: b?.selectedMemberIds || [],
            manualPlayers: b?.manualPlayers || []
          },
          result: b?.result || null
        };

        await supabase
          .from('brackets')
          .update(payload)
          .eq('id', b?.id);
      } catch {
        // ignore
      }
    }, 450);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [activeBracketId, brackets]);

  const updateActiveBracket = (patch) => {
    if (!activeBracketId) return;
    setBrackets(prev => prev.map(b => {
      if (b?.id !== activeBracketId) return b;
      return {
        ...b,
        ...patch,
        updatedAt: new Date().toISOString()
      };
    }));
  };

  const validateStep = (stepIndex) => {
    if (stepIndex === 0) {
      if (players.length < 4) {
        alert('최소 4명 이상 선택/등록해야 다음 단계로 진행할 수 있습니다.');
        return false;
      }
    }
    if (stepIndex === 1) {
      const nm = (bracketNameDraft || '').trim();
      if (!nm) {
        alert('대진표 이름을 입력해주세요.');
        return false;
      }
    }
    return true;
  };

  const handleStepClick = (idx) => {
    if (idx < currentStep) setCurrentStep(idx);
  };

  const handlePrevStep = () => {
    setCurrentStep(s => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNextStep = () => {
    if (!validateStep(currentStep)) return;
    if (currentStep === 2) {
      if (!result?.generated) {
        alert('대진을 먼저 생성해주세요.');
        return;
      }
    }
    setCurrentStep(s => Math.min(steps.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFinalize = async () => {
    if (!validateStep(0) || !validateStep(1)) return;
    if (!result?.generated) {
      alert('대진을 먼저 생성해주세요.');
      return;
    }

    if (!isNewDraft) {
      setCurrentStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsLoading(true);
    setLoadError('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) {
        alert('로그인이 필요합니다.');
        return;
      }

      const draft = brackets?.find(x => x?.id === 'draft');
      const payload = {
        client_id: userId,
        name: (bracketNameDraft || draft?.name || '대진표').trim(),
        sport: 'pickleball',
        config: { ...(draft?.config || {}), sport: 'pickleball' },
        participants: {
          selectedMemberIds: draft?.selectedMemberIds || [],
          manualPlayers: draft?.manualPlayers || []
        },
        result: draft?.result || null
      };

      const { data: created, error } = await supabase
        .from('brackets')
        .insert(payload)
        .select('*')
        .single();

      if (error) {
        alert(error?.message || '저장에 실패했습니다.');
        return;
      }

      navigate(`/session-list-dashboard/bracket/${created.id}`, { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  const gameMinutes = useMemo(() => {
    const base = defaultGameMinutesBySport?.pickleball || 15;
    if (!overrideGameMinutesEnabled) return base;
    const v = parseInt(overrideGameMinutes, 10);
    return Number.isFinite(v) && v > 0 ? v : base;
  }, [overrideGameMinutes, overrideGameMinutesEnabled, sport]);

  const maxRounds = useMemo(() => {
    const tm = parseInt(totalMinutes, 10);
    const gm = parseInt(gameMinutes, 10);
    if (!Number.isFinite(tm) || tm <= 0) return 0;
    if (!Number.isFinite(gm) || gm <= 0) return 0;
    return Math.floor(tm / gm);
  }, [gameMinutes, totalMinutes]);

  const players = useMemo(() => {
    const fromMembers = members
      ?.filter(m => selectedMemberIds?.includes(String(m?.id)))
      ?.map(m => ({ id: String(m?.id), name: m?.name, phone: m?.phone, source: 'member' }));

    const fromManual = manualPlayers?.map(p => ({ ...p, source: 'manual' }));

    const merged = [...fromMembers, ...fromManual];
    const uniq = new Map();
    merged.forEach(p => {
      uniq.set(p.id, p);
    });
    return Array.from(uniq.values());
  }, [manualPlayers, members, selectedMemberIds]);

  const toggleMember = (id) => {
    const sid = String(id);
    updateActiveBracket({
      selectedMemberIds: selectedMemberIds?.includes(sid)
        ? selectedMemberIds?.filter(x => x !== sid)
        : [...selectedMemberIds, sid]
    });
  };

  const addMemberId = (id) => {
    const sid = String(id);
    if (selectedMemberIds?.includes(sid)) return;
    updateActiveBracket({ selectedMemberIds: [...selectedMemberIds, sid] });
  };

  const addManual = () => {
    const name = manualName?.trim();
    const phone = manualPhone?.trim();
    if (!name) return;
    const id = `manual_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    updateActiveBracket({ manualPlayers: [...manualPlayers, { id, name, phone }] });
    setManualName('');
    setManualPhone('');
  };

  const handleManualPhoneChange = (e) => {
    const value = e?.target?.value?.replace(/[\D]/g, '') || '';
    const digits = value.slice(0, 11);
    let formatted = digits;
    if (digits.length <= 3) {
      formatted = digits;
    } else if (digits.length <= 7) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
    }
    setManualPhone(formatted);
  };

  const removeManual = (id) => {
    updateActiveBracket({ manualPlayers: manualPlayers?.filter(p => p?.id !== id) });
  };

  const selectedMembers = useMemo(() => {
    const map = new Map((members || []).map(m => [String(m?.id), m]));
    return (selectedMemberIds || [])
      .map(id => map.get(String(id)))
      .filter(Boolean);
  }, [members, selectedMemberIds]);

  const memberSearchResults = useMemo(() => {
    const q = (memberSearch || '').trim();
    if (!q) return members || [];
    const lower = q.toLowerCase();
    return (members || []).filter(m => {
      const nameOk = (m?.name || '').toLowerCase().includes(lower);
      const phoneOk = (m?.phone || '').includes(q);
      return nameOk || phoneOk;
    });
  }, [memberSearch, members]);

  const buildTextSummary = (generated) => {
    if (!generated) return '';
    const lines = [];
    lines.push('스포츠: 피클볼');
    lines.push(`1게임 예상 시간: ${gameMinutes}분`);
    lines.push(`총 경기 시간: ${totalMinutes}분`);
    lines.push(`코트 수: ${courtCount}`);
    lines.push(`생성 방식: ${modeOptions.find(o => o.value === mode)?.label || mode}`);
    if (mode === 'fixed_team') {
      lines.push(`대진표 방식: ${fixedTeamMatchTypeOptions.find(o => o.value === fixedTeamMatchType)?.label || fixedTeamMatchType}`);
    }
    lines.push(`홀수 처리: ${oddHandlingOptions.find(o => o.value === oddHandling)?.label || oddHandling}`);
    lines.push(`가능 라운드 수: ${generated.rounds?.length || 0}`);
    lines.push('');

    if (generated.teams?.length) {
      lines.push('팀 목록');
      generated.teams.forEach((t, idx) => {
        lines.push(`${idx + 1}. ${t.a.name} / ${t.b.name}`);
      });
      lines.push('');
    }

    generated.rounds.forEach((r) => {
      lines.push(`라운드 ${r.round}`);
      if (r.matches?.length) {
        r.matches.forEach((m, idx) => {
          const left = `${m.team1[0].name} / ${m.team1[1].name}`;
          const right = `${m.team2[0].name} / ${m.team2[1].name}`;
          lines.push(`- 코트 ${idx + 1}: ${left} vs ${right}`);
        });
      }
      if (r.resting?.length) {
        lines.push(`- 쉬는 인원: ${r.resting.map(p => p.name).join(', ')}`);
      }
      lines.push('');
    });

    return lines.join('\n');
  };

  const buildPlayerStats = (rounds) => {
    const played = new Map();
    const rested = new Map();
    (players || []).forEach(p => {
      played.set(p.id, 0);
      rested.set(p.id, 0);
    });

    (rounds || []).forEach(r => {
      (r?.matches || []).forEach(m => {
        (m?.team1 || []).concat(m?.team2 || []).forEach(p => {
          played.set(p.id, (played.get(p.id) || 0) + 1);
        });
      });
      (r?.resting || []).forEach(p => {
        rested.set(p.id, (rested.get(p.id) || 0) + 1);
      });
    });

    return (players || [])
      .map(p => ({
        id: p.id,
        name: p.name,
        phone: p.phone,
        played: played.get(p.id) || 0,
        rest: rested.get(p.id) || 0
      }))
      .sort((a, b) => {
        if (a.played !== b.played) return b.played - a.played;
        if (a.rest !== b.rest) return b.rest - a.rest;
        return (a.name || '').localeCompare(b.name || '');
      });
  };

  const buildResultSummary = (generated) => {
    const totalMatches = (generated?.rounds || []).reduce((acc, r) => acc + ((r?.matches || []).length), 0);
    const totalRested = (generated?.rounds || []).reduce((acc, r) => acc + ((r?.resting || []).length), 0);
    return {
      totalMatches,
      totalRested,
      roundCount: generated?.rounds?.length || 0
    };
  };

  const scorePairing = (a, b, partnerCounts) => partnerCounts.get(normalizeKey(a.id, b.id)) || 0;
  const scoreOpponent = (a, b, opponentCounts) => opponentCounts.get(normalizeKey(a.id, b.id)) || 0;

  const generateFixedTeam = () => {
    const cc = Math.max(1, parseInt(courtCount, 10) || 1);
    const roundCount = Math.max(0, maxRounds);

    const initial = shuffle(players);
    const evenCount = initial.length - (initial.length % 2);
    const pairable = initial.slice(0, evenCount);

    const teams = [];
    for (let i = 0; i < pairable.length; i += 2) {
      teams.push({ id: `t${i / 2}`, a: pairable[i], b: pairable[i + 1] });
    }

    const rounds = [];

    if (fixedTeamMatchType === 'round_robin') {
      const teamPairs = [];
      for (let i = 0; i < teams.length; i += 1) {
        for (let j = i + 1; j < teams.length; j += 1) {
          teamPairs.push([teams[i], teams[j]]);
        }
      }

      let cursor = 0;
      for (let r = 1; r <= roundCount; r += 1) {
        if (cursor >= teamPairs.length) break;
        const matches = [];
        const usedPlayerIds = new Set();

        for (let m = 0; m < cc; m += 1) {
          const pair = teamPairs[cursor];
          if (!pair) break;
          cursor += 1;
          matches.push({ team1: [pair[0].a, pair[0].b], team2: [pair[1].a, pair[1].b] });
          [pair[0].a, pair[0].b, pair[1].a, pair[1].b].forEach(p => usedPlayerIds.add(p.id));
        }

        const resting = (players || []).filter(p => !usedPlayerIds.has(p.id));
        rounds.push({ round: r, matches, resting });
      }

      const generated = { mode: 'fixed_team', teams, rounds };
      return {
        ...generated,
        meta: {
          playerStats: buildPlayerStats(rounds),
          summary: buildResultSummary(generated),
          configSnapshot: {
            sport,
            gameMinutes,
            totalMinutes,
            courtCount,
            oddHandling,
            mode,
            fixedTeamMatchType
          }
        }
      };
    }

    let teamQueue = [...teams];

    for (let r = 1; r <= roundCount; r += 1) {
      const maxMatches = Math.min(cc, Math.floor(teamQueue.length / 2));
      const matches = [];
      const usedTeamIds = new Set();

      for (let m = 0; m < maxMatches; m += 1) {
        const t1 = teamQueue.find(t => !usedTeamIds.has(t.id));
        if (!t1) break;
        usedTeamIds.add(t1.id);

        const t2 = teamQueue.find(t => !usedTeamIds.has(t.id));
        if (!t2) break;
        usedTeamIds.add(t2.id);

        matches.push({
          team1: [t1.a, t1.b],
          team2: [t2.a, t2.b]
        });
      }

      const restingTeams = teamQueue.filter(t => !usedTeamIds.has(t.id));
      const resting = restingTeams.flatMap(t => [t.a, t.b]);

      rounds.push({ round: r, matches, resting });

      const playedTeams = teamQueue.filter(t => usedTeamIds.has(t.id));
      const restTeams = teamQueue.filter(t => !usedTeamIds.has(t.id));
      teamQueue = [...restTeams, ...playedTeams];
    }

    const generated = { mode: 'fixed_team', teams, rounds };
    return {
      ...generated,
      meta: {
        playerStats: buildPlayerStats(rounds),
        summary: buildResultSummary(generated),
        configSnapshot: {
          sport,
          gameMinutes,
          totalMinutes,
          courtCount,
          oddHandling,
          mode,
          fixedTeamMatchType
        }
      }
    };
  };

  const generateRotation = () => {
    const cc = Math.max(1, parseInt(courtCount, 10) || 1);
    const roundCount = Math.max(0, maxRounds);

    const playedCounts = new Map();
    const partnerCounts = new Map();
    const opponentCounts = new Map();
    const restCounts = new Map();

    players.forEach(p => {
      playedCounts.set(p.id, 0);
      restCounts.set(p.id, 0);
    });

    const rounds = [];

    const evaluateCandidate = (cand, playedCountsNow) => {
      const W_PARTNER = 7;
      const W_OPPONENT = 4;
      const W_PLAY_IMBALANCE = 5;

      const partnerPenalty =
        scorePairing(cand.team1[0], cand.team1[1], partnerCounts) +
        scorePairing(cand.team2[0], cand.team2[1], partnerCounts);

      const opponentPenalty =
        scoreOpponent(cand.team1[0], cand.team2[0], opponentCounts) +
        scoreOpponent(cand.team1[0], cand.team2[1], opponentCounts) +
        scoreOpponent(cand.team1[1], cand.team2[0], opponentCounts) +
        scoreOpponent(cand.team1[1], cand.team2[1], opponentCounts);

      const nextCounts = new Map(playedCountsNow);
      cand.team1.concat(cand.team2).forEach(p => {
        nextCounts.set(p.id, (nextCounts.get(p.id) || 0) + 1);
      });

      const values = players.map(p => nextCounts.get(p.id) || 0);
      const maxV = Math.max(...values);
      const minV = Math.min(...values);
      const imbalance = maxV - minV;

      return partnerPenalty * W_PARTNER + opponentPenalty * W_OPPONENT + imbalance * W_PLAY_IMBALANCE;
    };

    for (let r = 1; r <= roundCount; r += 1) {
      const maxPlayersThisRound = Math.min(players.length, cc * 4);

      let pool = [...players];
      pool.sort((a, b) => {
        const ap = playedCounts.get(a.id) || 0;
        const bp = playedCounts.get(b.id) || 0;
        if (ap !== bp) return ap - bp;

        if (oddHandling === 'balanced') {
          const ar = restCounts.get(a.id) || 0;
          const br = restCounts.get(b.id) || 0;
          if (ar !== br) return ar - br;
        }

        return a.name.localeCompare(b.name);
      });

      if (oddHandling === 'random') {
        pool = shuffle(pool);
      }

      const selected = pool.slice(0, maxPlayersThisRound);
      const resting = pool.slice(maxPlayersThisRound);

      resting.forEach(p => {
        restCounts.set(p.id, (restCounts.get(p.id) || 0) + 1);
      });

      const matches = [];
      const local = [...selected];

      for (let m = 0; m < cc; m += 1) {
        if (local.length < 4) break;

        let best = null;
        let bestScore = Number.POSITIVE_INFINITY;

        for (let attempt = 0; attempt < Math.min(60, local.length * local.length); attempt += 1) {
          const four = shuffle(local).slice(0, 4);
          const [p1, p2, p3, p4] = four;

          const s1 = scorePairing(p1, p2, partnerCounts) + scorePairing(p3, p4, partnerCounts);
          const s2 = scorePairing(p1, p3, partnerCounts) + scorePairing(p2, p4, partnerCounts);
          const s3 = scorePairing(p1, p4, partnerCounts) + scorePairing(p2, p3, partnerCounts);

          const teamsCandidates = [
            { team1: [p1, p2], team2: [p3, p4], base: s1 },
            { team1: [p1, p3], team2: [p2, p4], base: s2 },
            { team1: [p1, p4], team2: [p2, p3], base: s3 }
          ];

          teamsCandidates.forEach(c => {
            const score = c.base * 3 + evaluateCandidate({ team1: c.team1, team2: c.team2 }, playedCounts);
            if (score < bestScore) {
              bestScore = score;
              best = { team1: c.team1, team2: c.team2 };
            }
          });
        }

        if (!best) {
          const four = local.splice(0, 4);
          best = { team1: [four[0], four[1]], team2: [four[2], four[3]] };
        }

        const usedIds = new Set([...best.team1, ...best.team2].map(p => p.id));
        const remaining = local.filter(p => !usedIds.has(p.id));
        local.splice(0, local.length, ...remaining);

        matches.push(best);

        best.team1.concat(best.team2).forEach(p => {
          playedCounts.set(p.id, (playedCounts.get(p.id) || 0) + 1);
        });

        partnerCounts.set(normalizeKey(best.team1[0].id, best.team1[1].id), (partnerCounts.get(normalizeKey(best.team1[0].id, best.team1[1].id)) || 0) + 1);
        partnerCounts.set(normalizeKey(best.team2[0].id, best.team2[1].id), (partnerCounts.get(normalizeKey(best.team2[0].id, best.team2[1].id)) || 0) + 1);

        best.team1.forEach(a => {
          best.team2.forEach(b => {
            opponentCounts.set(normalizeKey(a.id, b.id), (opponentCounts.get(normalizeKey(a.id, b.id)) || 0) + 1);
          });
        });
      }

      rounds.push({ round: r, matches, resting });
    }

    const generated = { mode: 'rotation', teams: null, rounds };
    return {
      ...generated,
      meta: {
        playerStats: buildPlayerStats(rounds),
        summary: buildResultSummary(generated),
        configSnapshot: {
          sport,
          gameMinutes,
          totalMinutes,
          courtCount,
          oddHandling,
          mode
        }
      }
    };
  };

  const handleGenerate = () => {
    if (!activeBracketId) return;
    if (players.length < 4) {
      updateActiveBracket({ result: { error: '최소 4명 이상 선택/등록해야 대진을 만들 수 있습니다.' } });
      return;
    }
    if (maxRounds <= 0) {
      updateActiveBracket({ result: { error: '총 경기 시간과 1게임 예상 시간을 확인해주세요.' } });
      return;
    }

    const generated = mode === 'fixed_team' ? generateFixedTeam() : generateRotation();
    updateActiveBracket({ result: { generated, summaryText: buildTextSummary(generated), error: null } });
    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGenerateAI = async () => {
    if (!activeBracketId) return;
    if (players.length < 4) {
      updateActiveBracket({ result: { error: '최소 4명 이상 선택/등록해야 AI 대진을 만들 수 있습니다.' } });
      return;
    }

    setIsAiGenerating(true);
    try {
      const payload = {
        players: (players || []).map(p => ({ id: String(p.id), name: p.name, phone: p.phone })),
        config: {
          ...activeBracket?.config,
          sport: 'pickleball',
          gameMinutes,
          totalMinutes,
          courtCount,
          oddHandling,
          mode,
          fixedTeamMatchType,
          format,
          displayLayout,
          startTime
        },
        prompt: aiPromptDraft
      };

      console.log('[AI Generate] 요청 payload:', payload);

      const { data, error } = await supabase.functions.invoke('bracket-generate', {
        body: payload
      });

      console.log('[AI Generate] 응답 data:', data);
      console.log('[AI Generate] 응답 error:', error);

      if (error) {
        console.error('[AI Generate] 에러 상세:', error);
        console.error('[AI Generate] 에러 메시지:', error?.message);
        console.error('[AI Generate] 에러 context:', error?.context);
        updateActiveBracket({ result: { error: error?.message || 'AI 생성에 실패했습니다.' } });
        return;
      }

      const generated = data?.generated;
      if (!generated) {
        console.error('[AI Generate] generated 없음, data:', data);
        updateActiveBracket({ result: { error: 'AI 응답이 비어있습니다.' } });
        return;
      }

      console.log('[AI Generate] 생성 성공:', generated);
      updateActiveBracket({ result: { generated, summaryText: buildTextSummary(generated), error: null } });
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (catchError) {
      console.error('[AI Generate] 예외 발생:', catchError);
      updateActiveBracket({ result: { error: catchError?.message || 'AI 생성 중 예외가 발생했습니다.' } });
    } finally {
      setIsAiGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.summaryText) return;
    try {
      await navigator.clipboard.writeText(result.summaryText);
      alert('대진표가 복사되었습니다');
    } catch (e) {
      alert('복사에 실패했습니다');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const shareUrl = useMemo(() => {
    if (!activeBracket) return '';
    const encoded = encodeBase64Url(activeBracket);
    if (!encoded) return '';
    return `${window.location.origin}/bracket-share?data=${encoded}`;
  }, [activeBracket]);

  const publicBracketUrl = useMemo(() => {
    if (!activeBracket) return '';
    const encoded = encodeBase64Url(activeBracket);
    if (!encoded) return '';
    return `${window.location.origin}/public-bracket?data=${encoded}`;
  }, [activeBracket]);

  const handleCopyShareUrl = async () => {
    if (!shareUrl) {
      alert('공유 링크 생성에 실패했습니다');
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('공유 링크가 복사되었습니다');
    } catch (e) {
      window.prompt('공유 링크를 복사하세요', shareUrl);
    }
  };

  const bracketShareUrl = useMemo(() => {
    return `${window.location.origin}/bracket-share?data=${encodeURIComponent(encodeBase64Url(shareData))}`;
  }, [shareData]);

  return (
    <OpenPlayToolsLayout userRole="player">
      <div>
        <div className="pt-6 md:pt-10 pb-[calc(84px+env(safe-area-inset-bottom,16px))] md:pb-6 px-4 max-w-6xl mx-auto">
          <div className="mb-6 md:mb-8">
          <button
            onClick={() => navigate('/session-list-dashboard/bracket')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth mb-4"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>대진표 목록</span>
          </button>
          </div>

        {isMemberPickerOpen && (
          <div
            className="fixed inset-0 z-300 flex items-start justify-center pt-[8vh] px-4 overflow-y-auto"
            onClick={(e) => {
              if (e?.target === e?.currentTarget) setIsMemberPickerOpen(false);
            }}
          >
            <div className="fixed inset-0 bg-background z-250" aria-hidden="true" />
            <div className="relative z-300 w-full max-w-2xl bg-card rounded-xl shadow-2xl border border-border my-8">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">회원정보 불러오기</h2>
                    <p className="text-sm caption text-muted-foreground">이름/전화번호로 검색하고 참가자를 선택하세요</p>
                  </div>
                  <button
                    onClick={() => setIsMemberPickerOpen(false)}
                    className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic active-press"
                    aria-label="닫기"
                    type="button"
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>
                <div className="space-y-3">
                <Input
                  label="검색"
                  type="text"
                  placeholder="예: 홍길동 또는 010"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                />

                <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-border">
                  {(memberSearchResults || []).length === 0 ? (
                    <div className="p-4 text-sm caption text-muted-foreground">검색 결과가 없습니다.</div>
                  ) : (
                    <div className="divide-y divide-border">
                      {(memberSearchResults || []).map((m) => {
                        const selected = (selectedMemberIds || []).includes(String(m?.id));
                        return (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => toggleMember(m.id)}
                            className="w-full text-left p-3 flex items-center justify-between gap-3 hover:bg-muted/40 transition-athletic"
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-foreground truncate">{m.name}</div>
                              <div className="text-xs caption text-muted-foreground mt-0.5 truncate">{m.phone}</div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {selected && (
                                <Icon name="Check" size={18} color="var(--color-primary)" />
                              )}
                              <div className={`text-xs font-medium px-2 py-1 rounded-md border ${selected ? 'bg-primary/10 border-primary/20 text-primary' : 'bg-muted border-border text-muted-foreground'}`}>
                                {selected ? '선택됨' : '선택'}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex flex-col md:flex-row gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsMemberPickerOpen(false)}
                    fullWidth
                  >
                    닫기
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => setIsMemberPickerOpen(false)}
                    fullWidth
                  >
                    완료
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isSessionPickerOpen && (
        <div
          className="fixed inset-0 z-300 flex items-start justify-center pt-[8vh] px-4 overflow-y-auto"
          onClick={(e) => {
            if (e?.target === e?.currentTarget) setIsSessionPickerOpen(false);
          }}
        >
          <div className="fixed inset-0 bg-background z-250" aria-hidden="true" />
          <div className="relative z-300 w-full max-w-2xl bg-card rounded-xl shadow-2xl border border-border my-8">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">매치에서 불러오기</h2>
                  <p className="text-sm caption text-muted-foreground">시작 전 매치를 선택하면 참가자를 불러옵니다</p>
                </div>
                <button
                  onClick={() => setIsSessionPickerOpen(false)}
                  className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic active-press"
                  aria-label="닫기"
                  type="button"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              {isLoadingSessions ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">매치 목록을 불러오는 중...</p>
                </div>
              ) : upcomingSessions.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="Calendar" size={40} className="mx-auto mb-3 text-muted-foreground" />
                  <p className="text-muted-foreground">예정된 매치가 없습니다</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto scrollbar-custom">
                  {upcomingSessions.map((session) => {
                    const participants = Array.isArray(session?.participants) ? session.participants : [];
                    const totalCount = participants.length;
                    return (
                      <button
                        key={session?.id}
                        type="button"
                        onClick={() => importSessionParticipants(session)}
                        className="w-full text-left bg-muted rounded-lg p-4 hover:bg-muted/70 transition-athletic active-press border border-border"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-heading font-medium text-foreground">{session?.title}</h4>
                          <span className="px-2 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary">
                            {totalCount}명
                          </span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Icon name="MapPin" size={14} />
                            <span>{session?.venue || '-'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Icon name="Calendar" size={14} />
                            <span>
                              {session?.date ? new Date(session.date).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' }) : '-'}
                              {session?.start_time ? ` ${session.start_time}` : ''}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => setIsSessionPickerOpen(false)}
                  fullWidth
                >
                  닫기
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isManualInputOpen && (
        <div
          className="fixed inset-0 z-300 flex items-start justify-center pt-[8vh] px-4 overflow-y-auto"
          onClick={(e) => {
            if (e?.target === e?.currentTarget) setIsManualInputOpen(false);
          }}
        >
          <div className="fixed inset-0 bg-background z-250" aria-hidden="true" />
          <div className="relative z-300 w-full max-w-md bg-card rounded-xl shadow-2xl border border-border my-8">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-2">수동 등록</h2>
                  <p className="text-sm caption text-muted-foreground">회원 목록에 없는 참가자를 직접 입력하세요</p>
                </div>
                <button
                  onClick={() => setIsManualInputOpen(false)}
                  className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic active-press"
                  aria-label="닫기"
                  type="button"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="space-y-3">
                <Input
                  label="이름"
                  type="text"
                  placeholder="이름 입력"
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                />
                <Input
                  label="전화번호"
                  type="text"
                  placeholder="010-1234-5678"
                  value={manualPhone}
                  onChange={handleManualPhoneChange}
                />
              </div>

              <div className="mt-4 pt-4 border-t border-border flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsManualInputOpen(false)}
                  fullWidth
                >
                  취소
                </Button>
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="right"
                  onClick={() => {
                    addManual();
                    setIsManualInputOpen(false);
                  }}
                  fullWidth
                >
                  추가
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

        </div>

        <main className="pt-0 pb-[calc(84px+env(safe-area-inset-bottom,16px))] md:pb-0">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-5 md:py-8">
            {currentStep === 3 && (
              <div className="max-w-2xl mx-auto">
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-success/10 rounded-md flex items-center justify-center flex-shrink-0">
                      <Icon name="CheckCircle" size={22} color="var(--color-success)" />
                    </div>
                    <h2 className="text-xl font-heading font-semibold text-foreground">완료</h2>
                    <p className="text-sm caption text-muted-foreground mt-1">대진표 저장/생성이 완료되었습니다.</p>
                  </div>
                </div>
              </div>
          )}

          {currentStep === 0 && (
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Download" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h2 className="text-lg font-heading font-semibold text-foreground">참가자 불러오기</h2>
                      <p className="text-xs caption text-muted-foreground">회원/매치/수동으로 추가</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      iconName="Users"
                      iconPosition="left"
                      onClick={() => setIsMemberPickerOpen(true)}
                      fullWidth
                    >
                      회원정보 불러오기
                    </Button>

                    <Button
                      variant="outline"
                      iconName="Calendar"
                      iconPosition="left"
                      onClick={openSessionPicker}
                      fullWidth
                    >
                      매치에서 불러오기
                    </Button>

                    <Button
                      variant="outline"
                      iconName="UserPlus"
                      iconPosition="left"
                      onClick={() => setIsManualInputOpen(true)}
                      fullWidth
                    >
                      수동 등록
                    </Button>
                  </div>

                  {members.length === 0 && (
                    <div className="mt-3 bg-muted rounded-md border border-border p-3 text-sm caption text-muted-foreground">
                      회원 데이터가 없습니다. /member-management에서 회원을 추가하면 불러올 수 있습니다.
                    </div>
                  )}
                </div>

                <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                        <Icon name="Users" size={20} color="var(--color-accent)" />
                      </div>
                      <div>
                        <h2 className="text-lg font-heading font-semibold text-foreground">추가된 참가자</h2>
                        <p className="text-xs caption text-muted-foreground">대진표에 포함될 인원</p>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 rounded-md bg-primary/10 text-primary text-sm font-medium">
                      {players.length}명
                    </span>
                  </div>

                  {players.length === 0 ? (
                    <div className="bg-muted rounded-md border border-border p-8 text-center">
                      <Icon name="Users" size={40} className="mx-auto mb-3 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">참가자를 추가해주세요</p>
                    </div>
                  ) : (
                    <div className="bg-muted/40 rounded-md border border-border p-3 max-h-72 overflow-y-auto scrollbar-custom">
                      <div className="space-y-2">
                        {selectedMembers.map(m => (
                          <div key={m.id} className="flex items-center justify-between gap-2 p-2 rounded-md bg-card border border-border">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{m.name}</p>
                              <p className="text-xs caption text-muted-foreground mt-0.5">{m.phone}</p>
                            </div>
                            <button
                              className="px-2 py-1 text-xs font-medium rounded-md border border-border bg-muted hover:bg-muted/70 transition-athletic active-press"
                              onClick={() => toggleMember(m.id)}
                              type="button"
                            >
                              제외
                            </button>
                          </div>
                        ))}
                        {manualPlayers.map(p => (
                          <div key={p.id} className="flex items-center justify-between gap-2 p-2 rounded-md bg-card border border-border">
                            <div className="min-w-0 flex items-center gap-2">
                              <span className="px-1.5 py-0.5 text-xs rounded bg-warning/10 text-warning">수동</span>
                              <div>
                                <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
                                {p.phone && <p className="text-xs caption text-muted-foreground mt-0.5">{p.phone}</p>}
                              </div>
                            </div>
                            <button
                              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic active-press"
                              onClick={() => removeManual(p.id)}
                              aria-label="삭제"
                              type="button"
                            >
                              <Icon name="X" size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="max-w-4xl mx-auto space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="FileText" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h2 className="text-lg font-heading font-semibold text-foreground">기본 정보</h2>
                      <p className="text-xs caption text-muted-foreground">대진표 이름 설정</p>
                    </div>
                  </div>
                  <Input
                    label="대진표 이름"
                    type="text"
                    value={bracketNameDraft}
                    onChange={(e) => setBracketNameDraft(e.target.value)}
                    onBlur={() => {
                      const next = (bracketNameDraft || '').trim();
                      if (!next) return;
                      updateActiveBracket({ name: next });
                    }}
                    placeholder="예: 2026-01-11 피클볼 친선전"
                  />
                </div>

                <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                      <Icon name="LayoutGrid" size={20} color="var(--color-accent)" />
                    </div>
                    <div>
                      <h2 className="text-lg font-heading font-semibold text-foreground">대진 형식</h2>
                      <p className="text-xs caption text-muted-foreground">방식 및 레이아웃</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <Select
                      label="대진 방식"
                      options={formatOptions}
                      value={format}
                      onChange={(v) => updateActiveBracket({
                        config: { ...activeBracket?.config, format: v }
                      })}
                    />
                    <Select
                      label="표현 레이아웃"
                      options={layoutOptions}
                      value={displayLayout}
                      onChange={(v) => updateActiveBracket({
                        config: { ...activeBracket?.config, displayLayout: v }
                      })}
                    />
                    {displayLayout === 'schedule' && (
                      <Input
                        label="시작 시간(HH:MM)"
                        type="text"
                        value={startTime}
                        onChange={(e) => updateActiveBracket({
                          config: { ...activeBracket?.config, startTime: e.target.value }
                        })}
                        placeholder="예: 14:00"
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                      <Icon name="Timer" size={20} color="var(--color-success)" />
                    </div>
                    <div>
                      <h2 className="text-lg font-heading font-semibold text-foreground">스포츠 & 시간</h2>
                      <p className="text-xs caption text-muted-foreground">종목 및 경기 시간</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-muted rounded-md border border-border p-3">
                      <p className="text-sm font-medium text-foreground">스포츠 종류</p>
                      <p className="text-xs caption text-muted-foreground mt-1">피클볼</p>
                    </div>
                    <div className="bg-muted rounded-md border border-border p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">기본 1게임 소요 시간</p>
                          <p className="text-xs caption text-muted-foreground mt-1">{defaultGameMinutesBySport?.[sport] || 15}분</p>
                        </div>
                        <Checkbox
                          label="수정"
                          checked={overrideGameMinutesEnabled}
                          onChange={(e) => updateActiveBracket({
                            config: { ...activeBracket?.config, overrideGameMinutesEnabled: e.target.checked }
                          })}
                        />
                      </div>
                      {overrideGameMinutesEnabled && (
                        <div className="mt-3">
                          <Input
                            label="1게임 소요 시간(분)"
                            type="number"
                            value={overrideGameMinutes}
                            onChange={(e) => updateActiveBracket({
                              config: { ...activeBracket?.config, overrideGameMinutes: e.target.value }
                            })}
                            placeholder="예: 15"
                          />
                        </div>
                      )}
                    </div>
                    <Input
                      label="총 경기 시간(분)"
                      type="number"
                      value={totalMinutes}
                      onChange={(e) => updateActiveBracket({
                        config: { ...activeBracket?.config, totalMinutes: e.target.value }
                      })}
                      placeholder="예: 120"
                    />
                    <div className="bg-primary/10 border border-primary/20 rounded-md p-3 flex items-start gap-2">
                      <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-muted-foreground">
                        {totalMinutes} ÷ {gameMinutes} = <span className="font-semibold text-foreground">{maxRounds}</span> 라운드
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                      <Icon name="Settings" size={20} color="var(--color-warning)" />
                    </div>
                    <div>
                      <h2 className="text-lg font-heading font-semibold text-foreground">경기 운영</h2>
                      <p className="text-xs caption text-muted-foreground">코트 및 생성 방식</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <Input
                      label="코트 수"
                      type="number"
                      value={courtCount}
                      onChange={(e) => updateActiveBracket({
                        config: { ...activeBracket?.config, courtCount: e.target.value }
                      })}
                      placeholder="예: 2"
                    />
                    <Select
                      label="인원이 홀수일 때 처리"
                      options={oddHandlingOptions}
                      value={oddHandling}
                      onChange={(v) => updateActiveBracket({
                        config: { ...activeBracket?.config, oddHandling: v }
                      })}
                    />
                    <Select
                      label="생성 방식"
                      options={modeOptions}
                      value={mode}
                      onChange={(v) => updateActiveBracket({
                        config: { ...activeBracket?.config, mode: v }
                      })}
                    />
                    {mode === 'fixed_team' && (
                      <Select
                        label="대진표 방식"
                        options={fixedTeamMatchTypeOptions}
                        value={fixedTeamMatchType}
                        onChange={(v) => updateActiveBracket({
                          config: { ...activeBracket?.config, fixedTeamMatchType: v }
                        })}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Sparkles" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h2 className="text-lg font-heading font-semibold text-foreground">AI 생성 (선택)</h2>
                    <p className="text-xs caption text-muted-foreground">예외사항/운영 룰을 입력하면 AI가 대진 구성 시 참고합니다</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <textarea
                    className="w-full min-h-[80px] resize-y rounded-md bg-muted border border-border px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="예외사항/요구사항을 입력하세요 (선택)"
                    value={aiPromptDraft}
                    onChange={(e) => setAiPromptDraft(e.target.value)}
                    onBlur={() => updateActiveBracket({
                      config: { ...activeBracket?.config, aiPrompt: aiPromptDraft }
                    })}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {[
                      '초보자 2명은 같은 팀이 되지 않게 해주세요.',
                      'A, B는 같은 코트에만 배정하고 싶어요.',
                      '처음 2라운드는 모두 최소 1번씩 뛰게 해주세요.',
                      '홀수일 때는 쉬는 사람이 균등하게 조정해주세요.'
                    ].map((ex) => (
                      <button
                        key={ex}
                        type="button"
                        className="text-left text-xs caption text-muted-foreground hover:text-foreground bg-muted border border-border rounded-md px-3 py-2 transition-athletic"
                        onClick={() => {
                          setAiPromptDraft(ex);
                          updateActiveBracket({ config: { ...activeBracket?.config, aiPrompt: ex } });
                        }}
                      >
                        {ex}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Button variant="default" iconName="Wand2" iconPosition="right" onClick={handleGenerate} fullWidth>
                    대진 생성
                  </Button>
                  <Button
                    variant="outline"
                    iconName="Sparkles"
                    iconPosition="right"
                    onClick={() => {
                      const prompt = (aiPromptDraft || '').trim();
                      if (!prompt) {
                        alert('AI 프롬프트 내용을 입력해주세요.');
                        return;
                      }
                      handleGenerateAI();
                    }}
                    fullWidth
                    disabled={isAiGenerating}
                  >
                    {isAiGenerating ? 'AI 생성 중...' : 'AI로 생성'}
                  </Button>
                </div>
                {result?.error && (
                  <div className="mt-3 bg-error/10 border border-error/20 rounded-md p-3 text-sm text-error">
                    {result.error}
                  </div>
                )}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="max-w-7xl mx-auto space-y-4">
              <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                      <Icon name="Trophy" size={20} color="var(--color-success)" />
                    </div>
                    <div>
                      <h2 className="text-lg font-heading font-semibold text-foreground">대진 결과</h2>
                      <p className="text-xs caption text-muted-foreground">
                        {result?.generated?.rounds ? `총 ${result.generated.rounds.length} 라운드 생성됨` : '대진표를 생성해주세요'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Copy"
                      iconPosition="left"
                      onClick={handleCopy}
                      disabled={!result?.summaryText}
                    >
                      복사
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Printer"
                      iconPosition="left"
                      onClick={handlePrint}
                    >
                      인쇄
                    </Button>
                  </div>
                </div>
              </div>

              {!result?.generated ? (
                <div className="bg-card border border-border rounded-xl p-6">
                  <BracketResultRenderer generated={null} config={{ ...activeBracket?.config, sport: 'pickleball' }} />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Icon name="SlidersHorizontal" size={20} color="var(--color-primary)" />
                        </div>
                        <div>
                          <h3 className="text-lg font-heading font-semibold text-foreground">설정 요약</h3>
                          <p className="text-xs caption text-muted-foreground">생성에 사용된 설정값</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-3 rounded-md bg-muted border border-border">
                          <span className="text-sm text-muted-foreground">생성 방식</span>
                          <span className="text-sm font-medium text-foreground">{modeOptions.find(o => o.value === mode)?.label || mode}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 rounded-md bg-muted border border-border">
                          <span className="text-sm text-muted-foreground">홀수 처리</span>
                          <span className="text-sm font-medium text-foreground">{oddHandlingOptions.find(o => o.value === oddHandling)?.label || oddHandling}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 rounded-md bg-muted border border-border">
                          <span className="text-sm text-muted-foreground">인원 / 코트</span>
                          <span className="text-sm font-medium text-foreground">{players.length}명 / {courtCount}코트</span>
                        </div>
                        {mode === 'fixed_team' && (
                          <div className="flex items-center justify-between p-3 rounded-md bg-muted border border-border">
                            <span className="text-sm text-muted-foreground">대진표 방식</span>
                            <span className="text-sm font-medium text-foreground">{fixedTeamMatchTypeOptions.find(o => o.value === fixedTeamMatchType)?.label || fixedTeamMatchType}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {result?.generated?.meta?.summary && (
                      <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                            <Icon name="BarChart3" size={20} color="var(--color-accent)" />
                          </div>
                          <div>
                            <h3 className="text-lg font-heading font-semibold text-foreground">결과 통계</h3>
                            <p className="text-xs caption text-muted-foreground">생성된 대진 요약</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="text-center p-4 rounded-lg bg-muted border border-border">
                            <p className="text-2xl font-heading font-bold text-foreground data-text">{result.generated.meta.summary.totalMatches}</p>
                            <p className="text-xs caption text-muted-foreground mt-1">총 경기 수</p>
                          </div>
                          <div className="text-center p-4 rounded-lg bg-muted border border-border">
                            <p className="text-2xl font-heading font-bold text-foreground data-text">{result.generated.meta.summary.roundCount}</p>
                            <p className="text-xs caption text-muted-foreground mt-1">라운드</p>
                          </div>
                          <div className="text-center p-4 rounded-lg bg-muted border border-border">
                            <p className="text-2xl font-heading font-bold text-foreground data-text">{result.generated.meta.summary.totalRested}</p>
                            <p className="text-xs caption text-muted-foreground mt-1">총 휴식(누적)</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {result?.generated?.meta?.playerStats?.length > 0 && (
                    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                          <Icon name="Users" size={20} color="var(--color-warning)" />
                        </div>
                        <div>
                          <h3 className="text-lg font-heading font-semibold text-foreground">참여/휴식 통계</h3>
                          <p className="text-xs caption text-muted-foreground">참가자별 경기 참여 및 휴식 횟수</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {result.generated.meta.playerStats.map(p => (
                          <div key={p.id} className="bg-muted border border-border rounded-md p-3 flex items-center justify-between">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
                              {p.phone && (
                                <p className="text-xs caption text-muted-foreground mt-0.5">{p.phone}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <div className="text-center">
                                <p className="text-lg font-semibold text-success data-text">{p.played}</p>
                                <p className="text-[10px] caption text-muted-foreground">참여</p>
                              </div>
                              <div className="text-center">
                                <p className="text-lg font-semibold text-muted-foreground data-text">{p.rest}</p>
                                <p className="text-[10px] caption text-muted-foreground">휴식</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {result?.generated?.teams?.length > 0 && (
                    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Icon name="UsersRound" size={20} color="var(--color-primary)" />
                        </div>
                        <div>
                          <h3 className="text-lg font-heading font-semibold text-foreground">팀 목록</h3>
                          <p className="text-xs caption text-muted-foreground">생성된 팀 조합 ({result.generated.teams.length}팀)</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {result.generated.teams.map((t, idx) => (
                          <div key={t.id} className="flex items-center gap-3 p-3 rounded-md bg-muted border border-border">
                            <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">{idx + 1}</span>
                            <span className="text-sm text-foreground">{t.a.name} / {t.b.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                        <Icon name="Table" size={20} color="var(--color-accent)" />
                      </div>
                      <div>
                        <h3 className="text-lg font-heading font-semibold text-foreground">대진표</h3>
                        <p className="text-xs caption text-muted-foreground">라운드별 경기 배치</p>
                      </div>
                    </div>
                    <BracketResultRenderer
                      generated={result.generated}
                      config={{
                        ...(activeBracket?.config || {}),
                        gameMinutes,
                        courtCount
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      </main>

      <div className="fixed bottom-0 left-0 right-0 md:relative md:bottom-auto bg-card border-t border-border md:border-0 p-4 md:p-0 z-100">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-2 md:mt-6">
          {currentStep === 1 && (
            <Button
              variant="outline"
              onClick={handlePrevStep}
              fullWidth
            >
              이전
            </Button>
          )}

          {currentStep === 0 && (
            <Button
              variant="default"
              onClick={handleNextStep}
              fullWidth
            >
              다음
            </Button>
          )}

          {currentStep === 2 && (
            <>
              <Button
                variant="outline"
                onClick={handlePrevStep}
                fullWidth
              >
                이전
              </Button>
              <Button
                variant="default"
                onClick={handleFinalize}
                fullWidth
              >
                최종 완료
              </Button>
            </>
          )}

          {currentStep === 3 && (
            <Button
              variant="default"
              onClick={() => navigate('/session-list-dashboard/bracket')}
              fullWidth
            >
              목록으로
            </Button>
          )}
        </div>
      </div>

      {isAiGenerating && (
        <div className="fixed inset-0 z-400 flex items-center justify-center">
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" aria-hidden="true" />
          <div className="relative z-410 bg-card border border-border rounded-xl p-6 shadow-2xl text-center">
            <div className="w-12 h-12 mx-auto mb-4 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
            <h3 className="text-lg font-heading font-semibold text-foreground mb-2">AI 대진표 생성 중...</h3>
            <p className="text-sm text-muted-foreground">잠시만 기다려주세요</p>
          </div>
        </div>
      )}

      </div>
    </OpenPlayToolsLayout>
  );
};

export default BracketBuilder;
